"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const ResumeContext = createContext({
  resumes: [],
});

export const ResumeProvider = ({ children }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Resumes
  const fetchResumes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/resumes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch resumes");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setResumes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch resumes");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch resumes");
    } finally {
      setLoading(false);
    }
  };

  // Create Resume
  const createResume = async (formData) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/resumes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create resume",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchResumes();

        toast.success(data.message || "Resume created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create resume",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create resume",
      };
    }
  };

  // Update Resume
  const updateResume = async (id, formData) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/resumes/${id}`, {
        method: "POST", // Change to PUT if your backend accepts multipart PUT
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update resume",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchResumes();

        toast.success(data.message || "Resume updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update resume",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update resume",
      };
    }
  };

  // Delete Resume
  const deleteResume = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/resumes/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete resume",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchResumes();

        toast.success(data.message || "Resume deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete resume",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete resume",
      };
    }
  };

  // Update Resume Status
  const updateStatus = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/resumes/status/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchResumes();

        toast.success(data.message || "Resume status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update status",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchResumes();
    }
  }, []);

  return (
    <ResumeContext.Provider
      value={{
        resumes,
        loading,
        error,
        fetchResumes,
        createResume,
        updateResume,
        deleteResume,
        updateStatus,
        setResumes,
      }}
    >
      {children}
    </ResumeContext.Provider>
  );
};

export const useResumes = () => useContext(ResumeContext);